<script setup lang="ts">
import { ref, toRefs, watch, useId } from 'vue';

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const props = defineProps<{
  modelValue: boolean;
  name: string;
  label?: string;
  disabled?: boolean;
}>();
const { name, modelValue, disabled } = toRefs(props);

const modelValueInternal = ref<boolean>(false);
const id = ref<string>(useId());

const handleChange = (): void => {
  emit('update:modelValue', modelValueInternal.value);
};

watch(
  modelValue,
  () => {
    modelValueInternal.value = modelValue.value;
  },
  { immediate: true },
);
</script>

<template>
  <div
    role="radio"
    :class="['flex items-center gap-sm', disabled && 'pointer-events-none cursor-default', 'shrink-0']"
    :aria-checked="modelValueInternal"
    @click.stop="handleChange()"
  >
    <div
      :tabindex="disabled ? -1 : 0"
      :class="[
        'flex items-center justify-center cursor-pointer',
        'size-icon-sm duration-300',
        'rounded-full border border-divider',
        'md:hover:bg-surface-secondary md:active:bg-surface-border',
        'focus-visible:outline-focus focus-visible:outline-offset-[3px]',
        'shrink-0',
        modelValueInternal ? 'bg-tint' : 'bg-white',
        disabled && '!bg-surface-tertiary',
      ]"
      @keypress.space.stop="handleChange()"
      @keypress.enter.stop="handleChange()"
    >
      <div v-if="modelValueInternal" :class="['size-[8px] rounded-full', disabled ? 'bg-secondary' : 'bg-white']"></div>
    </div>
    <label v-if="label" :for="id" class="md:whitespace-nowrap">
      {{ label }}
    </label>
    <input :id="id" type="radio" :name="name" :value="modelValueInternal" class="hidden" />
  </div>
</template>
